import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ACTIONS, EVENTS, STATUS, type EventData } from "react-joyride";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/lib/use-media-query";
import { getTourSteps, type TourStep } from "./tour-steps";
import { markTourSeen } from "./tour-storage";

const CALLOUT_TIMEOUT_MS = 6000;

export function useProductTour() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [justCompletedOnboarding, setJustCompletedOnboarding] = useState(false);
  // Passos de um tour de página (override do tour global) — null quando nenhum está ativo.
  const [overrideSteps, setOverrideSteps] = useState<TourStep[] | null>(null);
  // Guarda o índice a retomar depois de uma navegação disparada por um passo — null quando
  // não há navegação pendente.
  const pendingResumeIndex = useRef<number | null>(null);
  const prevOnboardingCompleted = useRef<boolean | undefined>(undefined);

  const globalSteps = useMemo(
    () => getTourSteps(user?.activeOrganization?.types ?? [], user?.activeOrganization?.isPersonal ?? false, isMobile),
    [user?.activeOrganization?.types, user?.activeOrganization?.isPersonal, isMobile],
  );
  const steps = overrideSteps ?? globalSteps;

  // Detecta a transição real false->true de onboardingCompleted dentro da mesma sessão do SPA —
  // não dispara numa leitura inicial já true, nem no fluxo voluntário de "Criar minha conta
  // pessoal" (que já parte de onboardingCompleted: true).
  useEffect(() => {
    const prev = prevOnboardingCompleted.current;
    const current = user?.onboardingCompleted;
    if (prev === false && current === true) {
      setJustCompletedOnboarding(true);
      const timeout = setTimeout(() => setJustCompletedOnboarding(false), CALLOUT_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
    prevOnboardingCompleted.current = current;
  }, [user?.onboardingCompleted]);

  const dismissCallout = useCallback(() => setJustCompletedOnboarding(false), []);

  // Retoma o tour assim que a rota de destino monta — um frame de folga garante que o `target`
  // já esteja no DOM antes do Joyride tentar localizá-lo.
  useEffect(() => {
    const idx = pendingResumeIndex.current;
    if (idx === null) return;
    if (steps[idx]?.route !== location.pathname) return;
    pendingResumeIndex.current = null;
    const raf = requestAnimationFrame(() => {
      setStepIndex(idx);
      setRun(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, steps]);

  const start = useCallback(() => {
    if (globalSteps.length === 0) return;
    markTourSeen();
    dismissCallout();
    // Só os passos que a pessoa enxerga: item de menu que o papel ou o plano esconde sairia como
    // passo pulado, e a contagem "Passo X de Y" mentiria. O menu e as abas existem em qualquer rota.
    const visiveis = globalSteps.filter((s) => s.target === "body" || s.id === "ajuda" || document.querySelector(s.target));
    setOverrideSteps(visiveis);
    const first = visiveis[0];
    setStepIndex(0);
    if (location.pathname !== first.route) {
      pendingResumeIndex.current = 0;
      navigate(first.route);
    } else {
      setRun(true);
    }
  }, [globalSteps, location.pathname, navigate, dismissCallout]);

  // Tour de uma página específica — nunca navega (todo passo já vive na rota atual).
  const startCustom = useCallback((customSteps: TourStep[]) => {
    if (customSteps.length === 0) return;
    setOverrideSteps(customSteps);
    setStepIndex(0);
    setRun(true);
  }, []);

  const encerrar = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    setOverrideSteps(null);
  }, []);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { status, action, index, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        encerrar();
        return;
      }

      // ESC e o X do tooltip chegam como ACTIONS.CLOSE. No Joyride, "close" significa "fecha o passo
      // e avança" (`dismissKeyAction`/`closeButtonAction`, ambos com default 'close'), não "encerra
      // o tour" — sem este ramo o evento caía no de STEP_AFTER abaixo, o índice avançava e o overlay
      // continuava cobrindo a tela, sem saída pelo teclado.
      if (action === ACTIONS.CLOSE) {
        encerrar();
        return;
      }

      // Alvo previsto pra essa tela mas ausente agora (aba errada, wizard em outro passo,
      // permissão sem o botão) — pula pro próximo passo em vez de matar o tour inteiro. Mesma
      // lógica de avanço do STEP_AFTER; se a cascata esvaziar os passos restantes, cai no "sem
      // próximo passo" abaixo e encerra normalmente.
      if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) return;

      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      const nextStep = steps[nextIndex];
      if (!nextStep) {
        encerrar();
        return;
      }

      if (nextStep.route !== location.pathname) {
        setRun(false);
        pendingResumeIndex.current = nextIndex;
        navigate(nextStep.route);
      } else {
        setStepIndex(nextIndex);
      }
    },
    [steps, location.pathname, navigate, encerrar],
  );

  return {
    steps,
    globalSteps,
    run,
    stepIndex,
    start,
    startCustom,
    handleEvent,
    justCompletedOnboarding,
    dismissCallout,
  };
}
